import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { CellValueChangedEvent } from 'ag-grid-community';
import { TFunction } from 'i18next';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import MultiSelection from '@/components/UI/MultiSelection/MultiSelection';
import OptionsCustom from '@/components/UI/OptionsCustom/OptionsCustom';
import Spinner from '@/components/UI/Spinner/Spinner';
import { CELL_EDITOR_TYPES, CELL_RENDERER_TYPES, FILTER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew, { ALIGN, TABLE_ROW_SELECTION } from '@/components/UI/TableListEditable/TableListEditableNew';
import { ASC_ORDER, CUSTOM_COLUMNS_TYPES, ENGLISH, GERMAN } from '@/helpers/constants';
import { langFormFormatter } from '@/helpers/tables-utils';
import { isValidEmail } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useToastContext from '@/hooks/useToastContext';
import notificationsService from '@/services/notificationsService';
import titleService from '@/services/titleService';
import { Email, MultiSelectionColumnConfig, OptionsCustomColumnConfig, OptionsRule, Variables, WorkflowRule, WorkflowRuleDto } from '@/types';
import styles from './Emails.module.scss';

const COLUMNS_INFO = {
  CURRENT_STATUS: { name: 'currentStatus', type: CUSTOM_COLUMNS_TYPES.DROPDOWN },
  PREVIOUS_STATUS_CONDITION: { name: 'previousStatusCondition', type: CUSTOM_COLUMNS_TYPES.DROPDOWN },
  PREVIOUS_STATUS: { name: 'previousStatus', type: CUSTOM_COLUMNS_TYPES.MULTI_SELECTION },
  ACTION_CONDITION: { name: 'actionCondition', type: CUSTOM_COLUMNS_TYPES.DROPDOWN },
  ACTION: { name: 'action', type: CUSTOM_COLUMNS_TYPES.MULTI_SELECTION },
  LEVEL: { name: 'level', type: CUSTOM_COLUMNS_TYPES.DROPDOWN },
  USE_CASE_CONDITION: { name: 'useCaseCondition', type: CUSTOM_COLUMNS_TYPES.DROPDOWN },
  USE_CASE: { name: 'useCase', type: CUSTOM_COLUMNS_TYPES.MULTI_SELECTION },
  CAMPAIGN_TYPE: { name: 'campaignType', type: CUSTOM_COLUMNS_TYPES.DROPDOWN },
  TO: { name: 'to', type: CUSTOM_COLUMNS_TYPES.OPTIONS_CUSTOM },
  CC: { name: 'cc', type: CUSTOM_COLUMNS_TYPES.OPTIONS_CUSTOM },
  TEMPLATE_ID: { name: 'templateId', type: CUSTOM_COLUMNS_TYPES.DROPDOWN },
  ACTIVE: { name: 'active', type: CUSTOM_COLUMNS_TYPES.CHECKBOX },
};

const ensureArray = (value) => Array.isArray(value) ? value : [];

const transformRulesDtoToState = (workflowsTable: WorkflowRuleDto[]) => workflowsTable.map((workflow) => ({
  ...workflow,
  templateId: workflow?.template?.id,
  campaignType: !workflow?.campaignType ? 'EMPTY' : workflow?.campaignType,
  actionCondition: !workflow?.actionCondition ? 'EMPTY' : workflow?.actionCondition,
  previousStatusCondition: !workflow?.previousStatusCondition ? 'EMPTY' : workflow?.previousStatusCondition,
  useCaseCondition: !workflow?.useCaseCondition ? 'EMPTY' : workflow?.useCaseCondition,
  previousStatus: ensureArray(workflow?.previousStatus),
  action: ensureArray(workflow?.action),
  useCase: ensureArray(workflow?.useCase),
  to: ensureArray(workflow?.to),
  cc: ensureArray(workflow?.cc),
}));

const validateNewOption = (newOption: string, existingOptions: string[], t: TFunction) => {
  if (!isValidEmail(newOption) && newOption !== '') {
    return [t('validations.formatEmail')];
  } else if (existingOptions.includes(newOption)) {
    return [t('validations.duplicateEmail')];
  }

  return [];
};

const columnsMandatory = ['templateId', 'currentStatus', 'level'];

const EMPTY_WORKFLOW = {
  templateId: '', // dropdown
  previousStatusCondition: '', // dropdown
  previousStatus: [], // multiselection
  actionCondition: '', // dropdown
  action: [], // multiselection
  currentStatus: '', // dropdown
  level: '', // dropdown
  useCase: [], // multiselection
  to: [], // multiselection of strings and variables
  cc: [], // multiselection of strings and variables
  useCaseCondition: '', // dropdown
  campaignType: '', // dropdown
  active: false, // boolean always false in creation
};

const EmailWorkflows: React.FC = () => {
  const { t } = useTranslation();
  const [workflows, setWorkflows] = useState<WorkflowRule[]>([]);
  const [options, setOptions] = useState<OptionsRule[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailTemplates, setEmailTemplates] = useState<Email[]>([]);
  const [emailVariables, setEmailVariables] = useState<Variables[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [popupOpen, setPopupOpen] = useState<string>('');
  const [newWorkflowData, setNewWorkflowData] = useState(EMPTY_WORKFLOW);
  const { addToast } = useToastContext();
  const lang = useAppLanguage();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      notificationsService.getEmailWorkflows(),
      notificationsService.getEmailWorkflowsFormOptions(),
      notificationsService.getEmailTemplates(),
      notificationsService.getEmailVariables(),
    ]).then((workflowsResponse) => {
      const [workflowsTable, optionsResponse, emailTemplates, emailVariables] = workflowsResponse;
      setWorkflows(transformRulesDtoToState(workflowsTable));
      setOptions({
        ...optionsResponse?.options,
        currentStatus: optionsResponse?.options?.status,
        previousStatus: optionsResponse?.options?.status,
      });
      setEmailTemplates(emailTemplates);
      setEmailVariables(emailVariables);
      setSelection([]);
    })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('menu.workflowSection'));
  }, [t, lang]);

  const getEmailWorkflows = () => {
    setLoading(true);
    notificationsService.getEmailWorkflows()
      .then((workflowsTable) => {
        setWorkflows(transformRulesDtoToState(workflowsTable));
        setSelection([]);
        setPopupOpen('');
        setNewWorkflowData(EMPTY_WORKFLOW);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleDeleteSelected = () => {
    setLoading(true);
    notificationsService.deleteEmailWorkflow(selection[0])
      .then(() => {
        setWorkflows(workflows.filter((workflow) => !selection.includes(workflow.id)));
        setSelection([]);
        setPopupOpen('');
        addToast(t('administration.emails.workflowDeleted'), 'Success');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const createCopyOfSelected = () => {
    setLoading(true);
    notificationsService.createCopyOfEmailWorkflow(selection[0])
      .then(() => {
        getEmailWorkflows();
        addToast(t('administration.emails.workflowCopied'), 'Success');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const updateWorkflow = useCallback((workflow: WorkflowRule, noUpdatedWorkflow: WorkflowRule) => {
    setLoading(true);
    notificationsService.updateEmailWorkflow(workflow.id, workflow)
      .then(() => null)
      .catch((error) => {
        // If the update fails, revert to the previous state
        const updatedWorkflows = workflows.map((w) => (w.id === workflow.id ? noUpdatedWorkflow : w));
        setWorkflows(updatedWorkflows);

        if (error.status === 422) {
          addToast(t('administration.emails.mandatoyFieldsAfterActivate'), 'Error');
        }
        console.log(error);
      })
      .finally(() => setLoading(false));
  }, [workflows, addToast, t]);

  const handleCreateNewRule = () => {
    setLoading(true);
    notificationsService.createEmailWorkflow(newWorkflowData)
      .then(() => {
        setPopupOpen('');
        getEmailWorkflows();
        addToast(t('administration.emails.newWorkflowCreated'), 'Success');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const templatesForm = useMemo(() => emailTemplates.reduce((acc, template) => {
    return {
      ...acc,
      [template.id]: {
        [ENGLISH]: template.title.EN,
        [GERMAN]: template.title.DE,
      },
    };
  }, {}), [emailTemplates]);

  const updateWorkflowField = useCallback((row: WorkflowRule, columnName: string, value: unknown) => {
    const updatedWorkflows = workflows.map((workflow) =>
      workflow.id === row.id ? { ...row, [columnName]: value } : workflow,
    );
    setWorkflows(updatedWorkflows);
    updateWorkflow(
      updatedWorkflows.find((workflow) => workflow.id === row.id) as WorkflowRule,
      workflows.find((workflow) => workflow.id === row.id) as WorkflowRule,
    );
  }, [workflows, updateWorkflow]);

  const handleEditRow = useCallback((event: CellValueChangedEvent<WorkflowRule>) => {
    const columnName = event.colDef.field;
    if (!columnName || !event.data || Array.isArray(event.oldValue)) return;

    updateWorkflowField(event.data, columnName, event.newValue);
  }, [updateWorkflowField]);

  const buildMultiSelection = useCallback((
    colName: string,
  ): MultiSelectionColumnConfig => ({
    options: options?.[colName]?.values || [],
    getAllSelectedData: (selected) => selected as string[],
    multiSelectionProps: { maxChip: 100, withoutTranslations: true, size: 'small' },
    onChange: ({ rowData, columnName, value }) => {
      updateWorkflowField(rowData as unknown as WorkflowRule, columnName, value);
    },
  }), [options, updateWorkflowField]);

  const getPropsForOptionsCustomColumns = useCallback((colName: string) => ({
    popupTitle: t('administration.emails.addCustomEmailFor', { columName: t(`administration.emails.${colName}`) }),
    buttonLabelAddCustom: t('administration.emails.addCustomEmail'),
    buttonLabelAddSelectableOption: t('administration.emails.addVariable'),
    inputLabelAddCustom: t('administration.emails.addCustomEmail'),
    descriptionOfCustomOption: t('administration.emails.customEmail'),
    tableTitle: t('administration.emails.existingOptions'),
    validateNewOption: (newOption: string, existingOptions: string[]) => validateNewOption(newOption, existingOptions, t),
    fieldName: t(`administration.emails.${colName}`),
  }), [t]);

  const buildOptionsCustom = useCallback((colName: string): OptionsCustomColumnConfig => ({
    defaultOptions: emailVariables,
    componentProps: getPropsForOptionsCustomColumns(colName),
    onChange: ({ rowData, columnName, value }) => {
      updateWorkflowField(rowData as unknown as WorkflowRule, columnName, value);
    },
  }), [emailVariables, updateWorkflowField, getPropsForOptionsCustomColumns]);

  const columns = useMemo(() => [
    {
      name: COLUMNS_INFO.CURRENT_STATUS.name,
      title: t('administration.emails.currentStatus'),
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: options?.[COLUMNS_INFO.CURRENT_STATUS.name]?.values || [],
      },
      flex: 2.2,
      wrapText: true,
    },
    {
      name: COLUMNS_INFO.PREVIOUS_STATUS_CONDITION.name,
      title: t('administration.emails.previousStatusCondition'),
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: options?.[COLUMNS_INFO.PREVIOUS_STATUS_CONDITION.name]?.values || [],
      },
      flex: 1.8,
    },
    {
      name: COLUMNS_INFO.PREVIOUS_STATUS.name,
      title: t('administration.emails.previousStatus'),
      multiSelection: buildMultiSelection(COLUMNS_INFO.PREVIOUS_STATUS.name),
      flex: 2.5,
      wrapText: true,
    },
    {
      name: COLUMNS_INFO.ACTION_CONDITION.name,
      title: t('administration.emails.actionCondition'),
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: options?.[COLUMNS_INFO.ACTION_CONDITION.name]?.values || [],
      },
      flex: 1.2,
    },
    {
      name: COLUMNS_INFO.ACTION.name,
      title: t('administration.emails.action'),
      multiSelection: buildMultiSelection(COLUMNS_INFO.ACTION.name),
      flex: 2.2,
      wrapText: true,
    },
    {
      name: COLUMNS_INFO.LEVEL.name,
      title: t('administration.emails.level'),
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: options?.[COLUMNS_INFO.LEVEL.name]?.values || [],
      },
      flex: 1.2,
    },
    {
      name: COLUMNS_INFO.USE_CASE_CONDITION.name,
      title: t('administration.emails.useCaseCondition'),
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: options?.[COLUMNS_INFO.USE_CASE_CONDITION.name]?.values || [],
      },
      flex: 1.5,
    },
    {
      name: COLUMNS_INFO.USE_CASE.name,
      title: t('administration.emails.useCase'),
      multiSelection: buildMultiSelection(COLUMNS_INFO.USE_CASE.name),
      flex: 2.5,
      wrapText: true,
    },
    {
      name: COLUMNS_INFO.CAMPAIGN_TYPE.name,
      title: t('administration.emails.campaignType'),
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: options?.[COLUMNS_INFO.CAMPAIGN_TYPE.name]?.values || [],
      },
      flex: 1.2,
    },
    {
      name: COLUMNS_INFO.TO.name,
      title: t('administration.emails.to'),
      optionsCustom: buildOptionsCustom(COLUMNS_INFO.TO.name),
      flex: 3.5,
      wrapText: true,
    },
    {
      name: COLUMNS_INFO.CC.name,
      title: t('administration.emails.cc'),
      optionsCustom: buildOptionsCustom(COLUMNS_INFO.CC.name),
      flex: 3.5,
      wrapText: true,
    },
    {
      name: COLUMNS_INFO.TEMPLATE_ID.name,
      title: t('administration.emails.template'),
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: Object.keys(templatesForm),
        formatValue: (value: string) => langFormFormatter(value, templatesForm, lang),
      },
      valueFormatter: ({ value }: { value: string }) => langFormFormatter(value, templatesForm, lang),
      flex: 3.5,
      wrapText: true,
    },
    {
      name: COLUMNS_INFO.ACTIVE.name,
      title: t('administration.emails.active'),
      align: ALIGN.CENTER,
      cellRenderer: CELL_RENDERER_TYPES.CHECKBOX,
      editable: true,
      cellEditor: CELL_EDITOR_TYPES.CHECKBOX,
    },
  ], [t, options, lang, buildMultiSelection, buildOptionsCustom, templatesForm]);

  const onSelectionChange = (selection: string[]) => {
    const lastSelected = selection.at(selection.length - 1);
    setSelection(lastSelected ? [lastSelected] : []);
  };

  return (
    <AccordionPanel title={`${t('menu.workflowSection')} ${workflows ? `(${workflows.length})` : ''}`} bodyCustomClass={styles.WorkFlowAccordion}>
      {(!options || emailTemplates.length === 0) && <Spinner isVisible isMask />}
      {(options && emailTemplates.length > 0) && (
        <>
          <div className={styles.ButtonsTable}>
            <div className={styles.ButtonsContentTableRight}>
              <Button
                id="btn-add-new-workflow"
                buttonStyle="TableButton"
                btnText={t('administration.emails.addNewWorkflow')}
                handleClick={() => setPopupOpen('CREATION')}
                disabled={loading}
              />
              <IcoButtonSimple
                disabled={selection.length !== 1 || loading}
                id="btn-copy-workflow"
                icon="icon-datei-doppelt-kopieren"
                tooltipText={t('copy')}
                handleClick={createCopyOfSelected}
              />
              <IcoButtonSimple
                disabled={selection.length !== 1 || loading}
                id="btn-delete-workflow"
                icon="icon-muelleimer-loeschen"
                tooltipText={t('dialog.delete')}
                handleClick={() => setPopupOpen('DELETION')}
              />
            </div>
          </div>
          <TableListEditableNew
            columnId="id"
            hasCheckbox
            rowSelectionMode={TABLE_ROW_SELECTION.SINGLE}
            selection={selection}
            onSelectionChange={(newSelection) => onSelectionChange(newSelection as string[])}
            list={workflows as (WorkflowRule & Record<string, unknown>)[]}
            columns={columns}
            onCellValueChanged={handleEditRow}
            loading={loading}
            hasMinWidth
          />
          {popupOpen === 'DELETION' && (
            <ConfirmDialog
              reject={() => setPopupOpen('')}
              confirm={handleDeleteSelected}
              title={`${t('dialog.deleteEmailWorkflow.title')}`}
              closeBtnText={t('dialog.cancel')}
              confirmBtnText={t('dialog.remove')}
            >
              <p className={styles.RemovePlantsMessage}>
                {`${t(`dialog.deleteEmailWorkflow.message`)}`}
              </p>
            </ConfirmDialog>
          )}
          {popupOpen === 'CREATION' && (
            <ConfirmDialog
              reject={() => setPopupOpen('')}
              confirm={handleCreateNewRule}
              confirmDisabled={columnsMandatory.some((col) => !newWorkflowData[col]) || loading}
              title={`${t('dialog.createEmailWorkflow.title')}`}
              closeBtnText={t('dialog.cancel')}
              confirmBtnText={t('dialog.add')}
            >
              <div className={styles.NewWorkFlowForm}>
                {Object.values(COLUMNS_INFO).map(({ name, type }) => (
                  <div key={`${name}-${type}`}>
                    {type === CUSTOM_COLUMNS_TYPES.DROPDOWN && (
                      <Dropdown
                        id={`email-workflow-${name}`}
                        variant="outlined"
                        options={name === 'templateId' ? templatesForm : options?.[name]?.values || []}
                        value={newWorkflowData?.[name]}
                        handleChange={(event) => setNewWorkflowData({ ...newWorkflowData, [name]: event.target.value })}
                        formControlClass="w-100"
                        withoutTranslations={name !== 'templateId'}
                        label={`${t(`administration.emails.${name}`)}`}
                        isMandatory={columnsMandatory.includes(name)}
                        disabled={loading}
                      />
                    )}
                    {type === CUSTOM_COLUMNS_TYPES.MULTI_SELECTION && (
                      <MultiSelection
                        id={`multiselection-${name}`}
                        handleDeleteItem={undefined}
                        options={options?.[name]?.values || []}
                        handleChange={(event) => setNewWorkflowData({ ...newWorkflowData, [name]: event.target.value })}
                        value={newWorkflowData?.[name] || []}
                        formControlClass="w-100"
                        variant="outlined"
                        label={`${t(`administration.emails.${name}`)}`}
                        withoutTranslations
                        isMandatory={columnsMandatory.includes(name)}
                        disabled={loading}
                      />
                    )}
                    {type === CUSTOM_COLUMNS_TYPES.OPTIONS_CUSTOM && (
                      <OptionsCustom
                        id={`options-custom-${name}`}
                        onChange={(newNal) => setNewWorkflowData({ ...newWorkflowData, [name]: newNal })}
                        value={newWorkflowData?.[name] || []}
                        defaultOptions={emailVariables}
                        disabled={loading}
                        {...getPropsForOptionsCustomColumns(name)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </ConfirmDialog>
          )}
        </>
      )}
    </AccordionPanel>
  );
};

export default EmailWorkflows;
